import { z } from "zod";

import { zDate } from "@/lib/zod/show-fragments";
import {
  zLeagueRef,
  zPlayerRef,
  zTeamRef,
} from "@/pages/sports/schemas/sports-fragments";
import type {
  IHeadToHeadMatch,
  IHighlightClip,
  ILineupPlayer,
  IMatchDetails,
  IMatchEvent,
  IMatchStat,
  ITeamLineup,
  ITopPerformer,
} from "@/types/match-details";

const zEvent: z.ZodType<IMatchEvent> = z
  .object({
    minute: z.string().nullish(),
    type: z.string().nullish(),
    detail: z.string().nullish(),
    teamId: z.string().nullish(),
    player: zPlayerRef.nullish(),
    assist: zPlayerRef.nullish(),
    playerOut: zPlayerRef.nullish(),
  })
  .passthrough();

const zStat: z.ZodType<IMatchStat> = z
  .object({
    key: z.string().nullish(),
    label: z.string().nullish(),
    home: z.number().nullish(),
    away: z.number().nullish(),
  })
  .passthrough();

const zLineupPlayer: z.ZodType<ILineupPlayer> = z
  .object({
    providerId: z.string().nullish(),
    name: z.string().nullish(),
    number: z.number().nullish(),
    position: z.string().nullish(),
  })
  .passthrough();

const zTeamLineup: z.ZodType<ITeamLineup> = z
  .object({
    formation: z.string().nullish(),
    startXI: z.array(zLineupPlayer).nullish(),
    substitutes: z.array(zLineupPlayer).nullish(),
  })
  .passthrough();

const zTopPerformer: z.ZodType<ITopPerformer> = z
  .object({
    player: zPlayerRef.nullish(),
    teamId: z.string().nullish(),
    rating: z.number().nullish(),
    goals: z.number().nullish(),
    assists: z.number().nullish(),
  })
  .passthrough();

const zH2HMatch: z.ZodType<IHeadToHeadMatch> = z
  .object({
    date: zDate,
    league: zLeagueRef.nullish(),
    leagueName: z.string().nullish(),
    homeTeam: zTeamRef.nullish(),
    awayTeam: zTeamRef.nullish(),
    homeScore: z.number().nullish(),
    awayScore: z.number().nullish(),
    state: z.string().nullish(),
  })
  .passthrough();

const zClip: z.ZodType<IHighlightClip> = z
  .object({
    title: z.string().nullish(),
    embedUrl: z.string().nullish(),
    url: z.string().nullish(),
    thumbnailUrl: z.string().nullish(),
    source: z.string().nullish(),
    channel: z.string().nullish(),
    publishedAt: zDate,
  })
  .passthrough();

/**
 * Read-only view schema for `GET /api/matches/{id}/details` — the whole
 * MatchDetailsDbDto. Each block is independently optional: a block never
 * ingested is absent, and a missing one must not hide the others. Enums (event
 * type, match state) stay plain strings so a new value still renders.
 */
export const matchDetailsViewSchema: z.ZodType<IMatchDetails> = z
  .object({
    report: z
      .object({
        score: z
          .object({
            home: z.number().nullish(),
            away: z.number().nullish(),
            penalties: z
              .object({
                home: z.number().nullish(),
                away: z.number().nullish(),
              })
              .passthrough()
              .nullish(),
          })
          .passthrough()
          .nullish(),
        events: z.array(zEvent).nullish(),
        statistics: z.array(zStat).nullish(),
        referee: z
          .object({
            name: z.string().nullish(),
            nationality: z.string().nullish(),
          })
          .passthrough()
          .nullish(),
        venueCapacity: z.number().nullish(),
      })
      .passthrough()
      .nullish(),
    reportUpdatedAt: zDate,
    lineups: z
      .object({
        home: zTeamLineup.nullish(),
        away: zTeamLineup.nullish(),
      })
      .passthrough()
      .nullish(),
    lineupsUpdatedAt: zDate,
    topPerformers: z.array(zTopPerformer).nullish(),
    topPerformersUpdatedAt: zDate,
    headToHead: z
      .object({
        played: z.number().nullish(),
        homeTeamWins: z.number().nullish(),
        awayTeamWins: z.number().nullish(),
        draws: z.number().nullish(),
        matches: z.array(zH2HMatch).nullish(),
      })
      .passthrough()
      .nullish(),
    headToHeadUpdatedAt: zDate,
    highlights: z.array(zClip).nullish(),
    highlightsUpdatedAt: zDate,
    finalizedAt: zDate,
  })
  .passthrough();
